import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subcontractorApi, type SubcontractorListParams } from '../services/subcontractorApi';
import type { CreateSubcontractorRequest, UpdateSubcontractorRequest } from '../types/service.types';

export const subcontractorKeys = {
  all: ['subcontractors'] as const,
  lists: () => [...subcontractorKeys.all, 'list'] as const,
  list: (params: SubcontractorListParams = {}) => [...subcontractorKeys.lists(), params] as const,
  detail: (id: string) => [...subcontractorKeys.all, 'detail', id] as const,
};

/**
 * Callers must pass an already-memoised `params` object — a fresh object
 * literal on every render changes the query key every render and refetches
 * forever.
 */
export function useSubcontractors(params: SubcontractorListParams = {}) {
  return useQuery({
    queryKey: subcontractorKeys.list(params),
    queryFn: () => subcontractorApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useSubcontractor(id: string) {
  return useQuery({
    queryKey: subcontractorKeys.detail(id),
    queryFn: () => subcontractorApi.get(id),
    enabled: !!id,
  });
}

/** Every mutation invalidates the whole catalogue key — list and detail alike. */
export function useCreateSubcontractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSubcontractorRequest) => subcontractorApi.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: subcontractorKeys.all }),
  });
}

export function useUpdateSubcontractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSubcontractorRequest }) =>
      subcontractorApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: subcontractorKeys.all }),
  });
}

export function useDeactivateSubcontractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => subcontractorApi.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: subcontractorKeys.all }),
  });
}

export function useReactivateSubcontractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => subcontractorApi.reactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: subcontractorKeys.all }),
  });
}
